/**
 * RevisionDiffViewer - Component to display before/after comparison of inspection revisions
 *
 * Used in ApprovalDashboard to help managers review what changes were made in a revision
 * Highlights modified fields with color coding
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  CompareArrows as DiffIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon
} from '@mui/icons-material';

/**
 * Display a single changed field with before/after values
 */
const ChangedField = ({ field, oldValue, newValue }) => (
  <TableRow>
    <TableCell>
      <Typography variant="body2" fontWeight="medium">
        {field}
      </Typography>
    </TableCell>
    <TableCell sx={{ bgcolor: 'error.light', color: 'error.dark' }}>
      <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
        {oldValue || '(empty)'}
      </Typography>
    </TableCell>
    <TableCell align="center">
      <ArrowIcon color="action" />
    </TableCell>
    <TableCell sx={{ bgcolor: 'success.light', color: 'success.dark' }}>
      <Typography variant="body2" fontWeight="bold">
        {newValue || '(empty)'}
      </Typography>
    </TableCell>
  </TableRow>
);

/**
 * Display a test result change with position and value changes
 */
const TestResultChange = ({ change, scaleLocation }) => (
  <TableRow>
    <TableCell>
      <Typography variant="body2" fontWeight="medium">
        {scaleLocation} - {change.test_position}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {change.field}
      </Typography>
    </TableCell>
    <TableCell sx={{ bgcolor: 'error.light', color: 'error.dark' }}>
      <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
        {change.old_value || '(empty)'}
      </Typography>
    </TableCell>
    <TableCell align="center">
      <ArrowIcon color="action" />
    </TableCell>
    <TableCell sx={{ bgcolor: 'success.light', color: 'success.dark' }}>
      <Typography variant="body2" fontWeight="bold">
        {change.new_value || '(empty)'}
      </Typography>
    </TableCell>
  </TableRow>
);

/**
 * Main RevisionDiffViewer component
 */
const RevisionDiffViewer = ({
  revision,
  originalSnapshot,
  scales = [],
  compact = false
}) => {
  // Parse changes from revision
  const changesMade = revision?.changes_made || {};
  const modifiedFields = changesMade.modified_fields || [];
  const modifiedResults = changesMade.modified_results || [];

  // No changes case
  if (modifiedFields.length === 0 && modifiedResults.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No changes detected in this revision.
      </Alert>
    );
  }

  // Helper to find scale location by ID
  const getScaleLocation = (scaleId) => {
    const scale = scales.find(s => s.id === scaleId);
    return scale?.location || `Scale ${scaleId?.substring(0, 8)}`;
  };

  // Count total changes
  const totalChanges = modifiedFields.length + modifiedResults.length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DiffIcon color="warning" />
        <Typography variant="subtitle1" fontWeight="bold">
          Revision Changes
        </Typography>
        <Chip
          label={`${totalChanges} change${totalChanges !== 1 ? 's' : ''}`}
          size="small"
          color="warning"
        />
      </Box>

      {/* Reason for revision */}
      {revision?.reason && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Reason for Revision:</Typography>
          <Typography variant="body2">{revision.reason}</Typography>
        </Alert>
      )}

      {/* Changes table */}
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size={compact ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell width="30%">
                <Typography variant="subtitle2">Field</Typography>
              </TableCell>
              <TableCell width="25%">
                <Typography variant="subtitle2" color="error">Original Value</Typography>
              </TableCell>
              <TableCell width="10%" align="center" />
              <TableCell width="25%">
                <Typography variant="subtitle2" color="success.dark">New Value</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Inspection-level field changes */}
            {modifiedFields.map((change, idx) => (
              <ChangedField
                key={`field-${idx}`}
                field={change.field}
                oldValue={change.old_value}
                newValue={change.new_value}
              />
            ))}

            {/* Divider if both field and result changes */}
            {modifiedFields.length > 0 && modifiedResults.length > 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Divider>
                    <Chip label="Test Results" size="small" />
                  </Divider>
                </TableCell>
              </TableRow>
            )}

            {/* Test result changes */}
            {modifiedResults.map((change, idx) => (
              <TestResultChange
                key={`result-${idx}`}
                change={change}
                scaleLocation={getScaleLocation(change.scale_id)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Revision metadata */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          Revision #{revision?.revision_number || 1}
        </Typography>
        {revision?.requested_at && (
          <Typography variant="caption" color="text.secondary">
            Requested: {new Date(revision.requested_at).toLocaleString()}
          </Typography>
        )}
        {revision?.status && (
          <Chip
            label={revision.status}
            size="small"
            color={
              revision.status === 'approved' ? 'success' :
              revision.status === 'rejected' ? 'error' :
              revision.status === 'pending_approval' ? 'warning' :
              'default'
            }
          />
        )}
      </Box>
    </Box>
  );
};

export default RevisionDiffViewer;
